import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';
import ReactDOM from 'react-dom';
import isEqual from 'react-fast-compare';

import getTestProps from 'test/lib/cypress/getTestProps';
import {BenchChipProps} from 'ui-library/chips/BenchChip';
import Chip, {ChipProps, ChipSize} from 'ui-library/chips/Chip';
import {LOCKED_ICON_CHIP_PROPS} from 'ui-library/chips/ChipUtil';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import TextInput from 'ui-library/inputs/TextInput';
import Menu from 'ui-library/menus/Menu';
import {OptionData, OptionValue} from 'ui-library/menus/MenuConstants';
import {
  defaultRenderOptionContent,
  getOptionRefForIndex,
  getSelectableIndices,
  hoverToSelectableIndex,
} from 'ui-library/menus/MenuUtil';
import OptionContainer from 'ui-library/menus/OptionContainer';
import SectionHeader from 'ui-library/menus/SectionHeader';
import withMenuNavigationLoading, {ConnectedMenuProps} from 'ui-library/menus/withMenuNavigationLoading';
import * as keyboard from 'util/Keyboard';
import {verticallyScrollItemIntoView} from 'util/Scroll';
import {BasicEvent, ElementKeyboardEvent, InputChangeEvent, InputElementFocusEvent} from 'util/Types';

import {Size, TextInputType} from './InputConstants';

interface ChipValueProps {
  text: string;
  size?: ChipSize;
}

interface BenchChipValueProps {
  id: string;
  size?: ChipSize;
}

export interface ChipSelectInputProps {
  autoFocus?: boolean;
  className?: string;
  chipClass?: React.ComponentType<ChipProps | BenchChipProps>;
  dropLeft?: boolean;
  getChipProps?: (optionValue: OptionValue) => ChipValueProps | BenchChipValueProps;
  isMulti?: boolean;
  onBlur?: () => void;
  onKeyDown?: (e: ElementKeyboardEvent) => void;
  onFocus?: (e: InputElementFocusEvent) => void;
  onQueryChange?: (e: InputChangeEvent) => void;
  onRemove?: (value: OptionValue) => void;
  onSelect?: ((value: OptionValue) => void) | null;
  optionDatas: Array<OptionData>;
  placeholder: string;
  query?: string;
  value: Array<OptionValue>;
  // Custom value equality check to be passed to Menu. Since not every menu element will have a value
  // (e.g. if the element is a loading placeholder), we support undefined values as parameters.
  isValueEqual?: (firstValue?: OptionValue, secondValue?: OptionValue) => boolean;
  /** Custom render for non-header OptionData. */
  renderOptionContent?: ((optionData: OptionData) => JSX.Element | string) | null;
  renderChips?: null | ((value: Array<OptionValue>) => JSX.Element | null);
  hideChips?: boolean;
  scrollable?: boolean;

  // No text input is rendered at all
  isReadOnly?: boolean;
  // A text input is rendered but disabled, meaning the user can't interact with it at all, including
  // selecting chips
  disabled?: boolean;
  // Just disables typing into the TextInput, but still allows selecting chips
  disableTyping?: boolean;

  // Optional button to show inline on right side of input
  button?: JSX.Element;
}

interface ChipSelectInputState {
  isChipFocused: boolean;
  isInputFocused: boolean;
}

class ChipSelectInput extends React.Component<
  ChipSelectInputProps & ConnectedMenuProps,
  ChipSelectInputState
> {
  state: ChipSelectInputState = {
    isChipFocused: false,
    isInputFocused: false,
  };

  static defaultProps: Partial<ChipSelectInputProps> = {
    autoFocus: true,
    chipClass: Chip,
    disabled: false,
    getChipProps: (optionValue: OptionValue) => ({text: String(optionValue)}),
    isReadOnly: false,
    renderChips: null,
  };

  private scrollAreaRef = React.createRef<Menu>();
  private optionRefs: {[ref: string]: OptionContainer<OptionValue>} = {};
  private inputRef = React.createRef<HTMLInputElement>();

  componentDidMount(): void {
    const selectableIndices = getSelectableIndices(this.props.optionDatas);
    this.props.onUpdateSelectableIndices(selectableIndices);
  }

  UNSAFE_componentWillReceiveProps(nextProps: ChipSelectInputProps): void {
    const oldSelectableIndices = getSelectableIndices(this.props.optionDatas);
    const newSelectableIndices = getSelectableIndices(nextProps.optionDatas);
    if (!isEqual(oldSelectableIndices, newSelectableIndices)) {
      this.props.onUpdateSelectableIndices(newSelectableIndices);
    }
  }

  componentDidUpdate = (prevProps: Readonly<ChipSelectInputProps & ConnectedMenuProps>): void => {
    // `isInputFocused` should always be false if `isReadOnly` or `disabled`. Otherwise things can get stuck
    // in a funky state. https://benchling.slack.com/archives/C0ANP64G7/p1604003740129500
    if (
      this.state.isInputFocused &&
      (this.props.isReadOnly || this.props.disabled) &&
      !prevProps.isReadOnly &&
      !prevProps.disabled
    ) {
      this.setState({isInputFocused: false});
    }
  };

  private canSelectAdditional(): boolean {
    return Boolean(!this.props.isReadOnly && (this.props.value.length === 0 || this.props.isMulti));
  }

  private handleBlurInput = (): void => {
    this.setState({isInputFocused: false});
    this.props.onBlur?.();
  };

  private handleFocusInput = (e: InputElementFocusEvent): void => {
    this.setState({isInputFocused: true});
    this.props.onFocus?.(e);
  };

  private handleBlurChip = (): void => {
    this.setState({isChipFocused: false});
  };

  private handleFocusChip = (): void => {
    this.setState({isChipFocused: true});
  };

  private renderOption(optionData: OptionData, index: number): JSX.Element {
    if (optionData.isHeader) {
      return (
        <SectionHeader className={optionData.className} key={index}>
          {optionData.name}
        </SectionHeader>
      );
    }

    const renderOptionContentFn = this.props.renderOptionContent || defaultRenderOptionContent;
    return (
      <OptionContainer
        className={optionData.className}
        key={index}
        value={optionData.value}
        onHover={() => {
          this.props.onHoverOption(index);
        }}
        onSelect={this.props.onSelect}
        isHovered={index === this.getSelectableIndexFromHover()}
        isDisabled={Boolean(optionData.disabled)}
        ref={(node: OptionContainer<OptionValue>) => {
          this.optionRefs[getOptionRefForIndex(index)] = node;
        }}
        skipIconOverride={optionData.skipIconOverride}
      >
        {renderOptionContentFn(optionData)}
      </OptionContainer>
    );
  }

  private renderChips(): JSX.Element | null {
    if (this.props.hideChips) {
      return null;
    }
    if (this.props.renderChips) {
      return this.props.renderChips(this.props.value);
    }
    return (
      <>
        {this.props.value.map((optionValue, index) => (
          <div key={index} className="chipSelectInput-chip">
            {this.renderOneChip(optionValue)}
          </div>
        ))}
      </>
    );
  }

  private renderOneChip(optionValue: OptionValue): JSX.Element {
    const ChipClass: React.ComponentType<BenchChipProps | ChipProps> = this.props.chipClass!;
    let chipProps: ChipValueProps | BenchChipValueProps = this.props.getChipProps!(optionValue);

    const isOptionReadOnly: boolean =
      optionValue && optionValue.hasOwnProperty('isReadOnly') && optionValue.isReadOnly;
    if (isOptionReadOnly) {
      chipProps = {...chipProps, ...LOCKED_ICON_CHIP_PROPS};
    }

    return (
      <ChipClass
        size={ChipSize.SMALL}
        onRemove={
          isOptionReadOnly || this.props.isReadOnly || this.props.disabled
            ? null
            : (e: BasicEvent) => {
                if (this.props.onRemove) {
                  this.props.onRemove(optionValue);
                  if (this.inputRef && this.inputRef.current) {
                    this.inputRef.current.focus();
                  }
                }
              }
        }
        onBlur={this.handleBlurChip}
        onFocus={this.handleFocusChip}
        {...chipProps}
      />
    );
  }

  private getSelectableIndexFromHover(index: number = this.props.hoverIndex): number {
    return hoverToSelectableIndex(this.props.selectableIndices, index);
  }

  private scrollIndexIntoView(hoverIndex: number): void {
    const selectableIndex = this.getSelectableIndexFromHover(hoverIndex);
    const scrollArea = ReactDOM.findDOMNode(this.scrollAreaRef.current) as Element;
    const itemBox = ReactDOM.findDOMNode(this.optionRefs[getOptionRefForIndex(selectableIndex)]) as Element;
    verticallyScrollItemIntoView(scrollArea, itemBox);
  }

  private changeHovered(newHoverIndex: number): void {
    const clampedHoverIndex = this.props.onChangeHoverIndex(newHoverIndex);
    this.scrollIndexIntoView(clampedHoverIndex);
  }

  private handleKeyDown = (e: ElementKeyboardEvent): void => {
    const hasSelectableOptions = getSelectableIndices(this.props.optionDatas).length;
    if (keyboard.isUpKey(e) && hasSelectableOptions) {
      this.changeHovered(this.props.hoverIndex - 1);
      e.preventDefault(); // Prevent up from moving text cursor to the beginning of line
    } else if (keyboard.isDownKey(e) && hasSelectableOptions) {
      e.preventDefault();
      this.changeHovered(this.props.hoverIndex + 1);
    } else if (keyboard.isEnterKey(e) && this.props.onSelect && hasSelectableOptions) {
      e.preventDefault();
      this.props.onSelect(this.props.optionDatas[this.getSelectableIndexFromHover()].value);
      if (this.props.isMulti && this.inputRef && this.inputRef.current) {
        this.inputRef.current.focus();
      }
    } else if (
      this.props.onRemove &&
      keyboard.isADeleteKey(e) &&
      this.props.value.length &&
      !this.props.query
    ) {
      e.preventDefault();
      this.props.onRemove(this.props.value[this.props.value.length - 1]);
      // If component didn't handle, allow parent to handle.
    } else if (this.props.onKeyDown) {
      this.props.onKeyDown(e);
    }
  };

  private shouldShowMenu = (): boolean => {
    const hasOptions = Boolean(this.props.optionDatas.length);
    return this.state.isInputFocused && this.canSelectAdditional() && hasOptions;
  };

  render(): JSX.Element {
    const canSelectAdditional = this.canSelectAdditional();
    return (
      <Dropdown
        className="chipSelectInput-dropdown"
        isOpen={this.shouldShowMenu()}
        allowMenuToCloseDropdown={false}
        menu={
          <Menu
            width={Menu.WIDTHS.FULL}
            height={Menu.HEIGHTS.MEDIUM}
            value={this.props.value}
            isValueEqual={this.props.isValueEqual}
            isMulti={true}
            ref={this.scrollAreaRef}
            dropLeft={this.props.dropLeft}
            scrollable={this.props.scrollable}
          >
            {this.props.optionDatas.map((optionData: OptionData, index: number) =>
              this.renderOption(optionData, index)
            )}
          </Menu>
        }
      >
        <div
          className={classNames(this.props.className, 'u-flex-display')}
          {...getTestProps({component: 'ChipSelectInput', element: 'wrapper'})}
        >
          <div
            className={classNames('chipSelectInput', 'u-flex-grow', {
              'is-child-focused':
                !this.props.isReadOnly && (this.state.isInputFocused || this.state.isChipFocused),
              'is-disabled': this.props.disabled,
              'chipSelectInput--inline': Boolean(this.props.button),
            })}
          >
            {this.renderChips()}
            {!this.props.isReadOnly && (
              <TextInput
                autoFocus={this.props.autoFocus}
                className={classNames('chipSelectInput-input', 'u-flex-grow', {
                  [css(styles['chipSelectInput-input--cannotSelectAdditional'])]: !canSelectAdditional,
                })}
                disabled={this.props.disabled}
                readOnly={this.props.disableTyping}
                placeholder={canSelectAdditional ? this.props.placeholder : ''}
                size={Size.SMALL}
                type={TextInputType.TEXT}
                inputRef={this.inputRef}
                onBlur={this.handleBlurInput}
                onChange={this.props.onQueryChange}
                onFocus={this.handleFocusInput}
                onKeyDown={this.handleKeyDown}
                value={this.props.query}
                dataTestAttrs={{component: 'ChipSelectInput', element: 'textInput'}}
              />
            )}
          </div>
          {this.props.button && React.cloneElement(this.props.button, {className: 'button--inline'})}
        </div>
      </Dropdown>
    );
  }
}

const styles = StyleSheet.create({
  // Keeps chip on one line when only one chip can be selected:
  // - Override the min-width to a low value. This way, the chip takes up most of the line but the cursor is
  //   still always visible.
  'chipSelectInput-input--cannotSelectAdditional': {minWidth: 5, padding: 0},
});

export default withMenuNavigationLoading(ChipSelectInput);
